import type { EvalFramework } from './eval-frameworks'
import type { ILlm } from '../llms/llms-store.svelte'
import type { BiasType, IUnlearningRun } from '../runs/llm-run.svelte'

enum LlmEvalStatus {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED',
  IGNORED = 'IGNORED',
  REJECTED = 'REJECTED',
}

const displayEvalStatus: Record<LlmEvalStatus, string> = {
  [LlmEvalStatus.PENDING]: 'Pending',
  [LlmEvalStatus.RECEIVED]: 'Received',
  [LlmEvalStatus.STARTED]: 'Started',
  [LlmEvalStatus.SUCCESS]: 'Success',
  [LlmEvalStatus.FAILURE]: 'Failure',
  [LlmEvalStatus.RETRY]: 'Retry',
  [LlmEvalStatus.REVOKED]: 'Cancelled',
  [LlmEvalStatus.IGNORED]: 'Ignored',
  [LlmEvalStatus.REJECTED]: 'Rejected',
}

const displayEvalStatusSubtitle: Record<LlmEvalStatus, string> = {
  [LlmEvalStatus.PENDING]: 'Evaluation not yet started: Provisioning compute',
  [LlmEvalStatus.RECEIVED]: 'Evaluation request acknowledged',
  [LlmEvalStatus.STARTED]: 'Evaluation running',
  [LlmEvalStatus.SUCCESS]: '',
  [LlmEvalStatus.FAILURE]: 'Evaluation failed. Review logs for details',
  [LlmEvalStatus.RETRY]: 'Evaluation retrying',
  [LlmEvalStatus.REVOKED]: 'Evaluation cancelled',
  [LlmEvalStatus.IGNORED]: 'Evaluation ignored',
  [LlmEvalStatus.REJECTED]: 'Evaluation rejected. Try again',
}

type JudgeModel = {
  pathOrRepoId?: string
  token?: string
  tokenId?: number
  batchSize?: number
  outputTokens?: number
  use4bit?: boolean
}

type PresetType =
  | 'BBQ_BIAS'
  | 'BBQ_UNBIAS'
  | 'UNQOVER_BIAS'
  | 'HALU_EVAL'
  | 'MED_HALLU'
  | 'INJECTION_EVAL'
  | 'XSTEST'
  | 'OR_BENCH'

type LaunchEvalPayload = {
  organizationId?: number
  name: string
  modelId?: number
  sourceRunId?: string
  presetType?: PresetType
  biasType?: BiasType
  judgeModel?: JudgeModel
  framework?: EvalFramework
  taskIds?: string[]
  sampleLimit?: number | null // `null` launches every sample of every selected task.
  maxModelLen?: number | null // `null` uses the model's configured context length.
}

interface ILlmEvalRun {
  id: number
  runId: string
  name: string
  model: ILlm | null
  sourceRun: IUnlearningRun | null
  creatorId: number | null
  creatorName: string | null
  framework: EvalFramework
  presetType: PresetType | null
  biasType?: BiasType | null
  taskIds: string[] | null
  sampleLimit: number | null
  attemptTimeout?: number | null
  maxRetries?: number | null
  maxModelLen: number | null
  status: LlmEvalStatus
  mlflowRunId: string | null
  createdAt: string
}

const formatEnumLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getStatusLabel = (status: LlmEvalStatus) =>
  displayEvalStatus[status] ?? formatEnumLabel(status)

export type { ILlmEvalRun, JudgeModel, LaunchEvalPayload, PresetType }
export {
  displayEvalStatus,
  displayEvalStatusSubtitle,
  formatEnumLabel,
  getStatusLabel,
  LlmEvalStatus,
}
